import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Receipt as InvoiceIcon,
  Logout as LogoutIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { requestService, Request } from '../../services/requestService';
import { serviceProviderPortalService } from '../../services/serviceProviderPortalService';
import authService from '../../services/auth.service';

const ServiceProviderDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [user, setUser] = useState<any>(null);
  const [serviceProviderId, setServiceProviderId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    // Check if user is service provider
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/service-provider/login');
      return;
    }

    const roleType = currentUser?.role?.type || currentUser?.role?.attributes?.type;
    // Accept both 'service-provider' and 'service_provider' (hyphen or underscore)
    if (roleType !== 'service-provider' && roleType !== 'service_provider') {
      authService.logout();
      navigate('/service-provider/login');
      return;
    }

    setUser(currentUser);
    loadServiceProvider(currentUser.id);
  }, []);

  const loadServiceProvider = async (userId: number) => {
    try {
      setLoading(true);
      const response = await serviceProviderPortalService.getMyProfile();
      
      if (response.data && response.data.length > 0) {
        const providerId = response.data[0].id;
        setServiceProviderId(providerId);
        loadRequests(providerId);
      } else {
        setError('Service provider profile not found. Please contact administrator.');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Error loading service provider:', err);
      setError('Failed to load your profile.');
      setLoading(false);
    }
  };

  const loadRequests = async (providerId: number) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await serviceProviderPortalService.getMyRequests({
        sort: 'createdAt:desc',
      });
      
      setRequests(response.data?.data || []);
    } catch (err: any) {
      console.error('Error loading requests:', err);
      setError('Failed to load requests.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
    localStorage.removeItem('isServiceProvider');
    navigate('/service-provider/login');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'active':
        return 'info';
      case 'assigned':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Filter requests based on active tab
  const { activeRequests, finishedRequests } = useMemo(() => {
    const active = requests.filter((request) => {
      const attrs = (request as any).attributes || request;
      const status = attrs.status || request.status;
      return status !== 'cancelled' && status !== 'completed';
    });
    
    const finished = requests.filter((request) => {
      const attrs = (request as any).attributes || request;
      const status = attrs.status || request.status;
      return status === 'cancelled' || status === 'completed';
    });
    
    return { activeRequests: active, finishedRequests: finished };
  }, [requests]);

  const filteredRequests = activeTab === 0 ? activeRequests : finishedRequests;

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#F56A2C', color: 'white' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Service Provider Portal
            </Typography>
            <Typography variant="body2">
              Welcome, {user.firstName || user.username}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            Logout
          </Button>
        </Stack>
      </Paper>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" fontWeight="bold">
              My Requests
            </Typography>
          </Stack>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label={`Active (${activeRequests.length})`} />
              <Tab label={`Finished (${finishedRequests.length})`} />
            </Tabs>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Request #</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Reasons</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Invoice</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {activeTab === 0 
                            ? 'No active requests assigned to you.' 
                            : 'No finished requests.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRequests.map((request) => {
                      const attrs = (request as any).attributes || request;
                      const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient;
                      const invoice = attrs.invoice?.data?.attributes || attrs.invoice;
                      const reasons = attrs.reasons || request.reasons || [];
                      
                      const status = attrs.status || request.status;
                      
                      return (
                        <TableRow key={request.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {attrs.requestNumber || request.requestNumber || `#${request.id}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 300 }}>
                              {attrs.description || request.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={status || 'pending'}
                              color={getStatusColor(status || 'pending')}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {reasons.length > 0 ? (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                {reasons.slice(0, 2).map((reason: string, idx: number) => (
                                  <Chip
                                    key={idx}
                                    label={reason}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ))}
                                {reasons.length > 2 && (
                                  <Chip
                                    label={`+${reasons.length - 2}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                )}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {invoice ? (
                              <Chip
                                icon={<InvoiceIcon />}
                                label={invoice.invoiceNumber || 'Invoice'}
                                color="success"
                                size="small"
                              />
                            ) : activeTab === 0 ? (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => navigate(`/service-provider/invoice/${request.id}`)}
                              >
                                Create
                              </Button>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              {activeTab === 0 && (status === 'assigned') && (
                                <>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    onClick={async () => {
                                      try {
                                        await serviceProviderPortalService.acceptRequest(request.id);
                                        loadRequests(serviceProviderId!);
                                      } catch (err: any) {
                                        setError(err.response?.data?.error?.message || 'Failed to accept request');
                                      }
                                    }}
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={async () => {
                                      const reason = window.prompt('Reason for rejection:');
                                      if (!reason) return;
                                      try {
                                        await serviceProviderPortalService.rejectRequest(request.id, reason);
                                        loadRequests(serviceProviderId!);
                                      } catch (err: any) {
                                        setError(err.response?.data?.error?.message || 'Failed to reject request');
                                      }
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/service-provider/request/${request.id}`)}
                                title="View Details"
                              >
                                <ViewIcon />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ServiceProviderDashboard;

